import { Suspense, useEffect, Fragment, type ReactNode } from 'react';
import { Route, Navigate, useParams, useLocation, Outlet, useRouteError, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import { Layout } from './components/Layout';
import { SlideDirectionBridge } from './components/SlideDirectionBridge';
import { ThemeApplier } from './components/ThemeApplier';
import { lazyWithPreload } from './lib/lazyWithPreload';
import { track, reportError } from './lib/observability';
import { reportClientError } from './lib/error-reporter';
import { isStaleChunkError, maybeReloadForStaleChunk } from './lib/chunkReload';
import { trackActivity } from './lib/api';
import { setGuidesRoute, entitySkeletonFromPath } from './lib/guidesContextStore';
import { RequirePermission } from './components/auth/RequirePermission';
import { INSIGHTS_ACCESS_PERMS, useAuthStore } from './lib/authStore';
import { ToastContainer } from './components/ToastContainer';
import { AnalysisPanel } from './components/AnalysisPanel';
import { SyncLogPanel } from './components/SyncLogPanel';
import { RealtimeBridge } from './components/RealtimeBridge';
import { ReloadPrompt } from './components/ReloadPrompt';
import { SupportChatWidget } from './components/SupportChatWidget';
import { SyncActivityBridge } from './components/activity/SyncActivityBridge';
import { ActivityCenter } from './components/activity/ActivityCenter';
import { OnboardingGate } from './components/onboarding/OnboardingGate';
import { useActivityCenterFlag } from './lib/featureFlags';
import { syncPushSubscription } from './lib/push';

// Every page is code-split so the entry bundle stays small — the three rich
// editors (Monaco, TinyMCE, Milkdown), the chat kit, charts and the diff viewer
// only download when you actually open the screen that uses them. The app shell
// (Layout + the always-mounted overlays above) is the only eager UI.
// lazyWithPreload also gives each page a `.preload()` (used to warm the detail
// chunk before the View Transition slide).
const lazyPage = (
  loader: () => Promise<Record<string, unknown>>,
  name: string,
) => lazyWithPreload(loader, name);

const Dashboard = lazyPage(() => import('./pages/Dashboard'), 'Dashboard');
const Integrations = lazyPage(() => import('./pages/Integrations'), 'Integrations');
const AiChat = lazyPage(() => import('./pages/AiChat'), 'AiChat');
const Settings = lazyPage(() => import('./pages/Settings'), 'Settings');
const UserDetail = lazyPage(() => import('./pages/UserDetail'), 'UserDetail');
const Profile = lazyPage(() => import('./pages/Profile'), 'Profile');
const ThemeEditor = lazyPage(() => import('./pages/ThemeEditor'), 'ThemeEditor');
const Discourse = lazyPage(() => import('./pages/Discourse'), 'Discourse');
// Exported so the Topics list can DiscourseTopic.preload() before its slide
// View Transition (warms the chunk so the slide shows the real page).
export const DiscourseTopic = lazyPage(() => import('./pages/DiscourseTopic'), 'DiscourseTopic');
const BacklogItems = lazyPage(() => import('./pages/BacklogItems'), 'BacklogItems');
const Companies = lazyPage(() => import('./pages/Companies'), 'Companies');
const CommsInbox = lazyPage(() => import('./pages/CommsInbox'), 'CommsInbox');
const CompanyDetail = lazyPage(() => import('./pages/CompanyDetail'), 'CompanyDetail');
const ContactDetail = lazyPage(() => import('./pages/ContactDetail'), 'ContactDetail');
const DealDetail = lazyPage(() => import('./pages/DealDetail'), 'DealDetail');
const LeadDetail = lazyPage(() => import('./pages/LeadDetail'), 'LeadDetail');
// Exported so the backlog board can BacklogItemDetail.preload() before its
// slide View Transition (warms the chunk so the slide shows the real page).
export const BacklogItemDetail = lazyPage(() => import('./pages/BacklogItemDetail'), 'BacklogItemDetail');
// Exported so a Related-items row can FeedbackDetail.preload() before its slide,
// the same way the backlog + topic details are warmed. (f-01842)
export const FeedbackDetail = lazyPage(() => import('./pages/FeedbackDetail'), 'FeedbackDetail');
const ReleasePlanning = lazyPage(() => import('./pages/ReleasePlanning'), 'ReleasePlanning');
const Entitlements = lazyPage(() => import('./pages/Entitlements'), 'Entitlements');
const ReleaseAppDetail = lazyPage(() => import('./pages/ReleaseAppDetail'), 'ReleaseAppDetail');
const ReleaseTrackingDetail = lazyPage(() => import('./pages/ReleaseTrackingDetail'), 'ReleaseTrackingDetail');
const ReleaseSession = lazyPage(() => import('./pages/ReleaseSession'), 'ReleaseSession');
const CommitTracker = lazyPage(() => import('./pages/CommitTracker'), 'CommitTracker');
const CommitDetails = lazyPage(() => import('./pages/CommitDetails'), 'CommitDetails');
const CommitAnalysis = lazyPage(() => import('./pages/CommitAnalysis'), 'CommitAnalysis');
const InsightsHub = lazyPage(() => import('./pages/InsightsHub'), 'InsightsHub');
const EmployeeDetail = lazyPage(() => import('./pages/EmployeeDetail'), 'EmployeeDetail');
const KnowledgeBase = lazyPage(() => import('./pages/KnowledgeBase'), 'KnowledgeBase');
const KnowledgeArticleEditor = lazyPage(() => import('./pages/KnowledgeArticleEditor'), 'KnowledgeArticleEditor');
const SolutionDesign = lazyPage(() => import('./pages/SolutionDesign'), 'SolutionDesign');
// Exported so the Solution Design list can PageSpec.preload() before its slide
// View Transition (mirrors KnowledgeArticleReader).
export const PageSpec = lazyPage(() => import('./pages/PageSpec'), 'PageSpec');
// Exported so the Articles list can KnowledgeArticleReader.preload() before its
// slide View Transition (mirrors BacklogItemDetail).
export const KnowledgeArticleReader = lazyPage(() => import('./pages/KnowledgeArticleReader'), 'KnowledgeArticleReader');
const CocoConfig = lazyPage(() => import('./pages/CocoConfig'), 'CocoConfig');
const Activity = lazyPage(() => import('./pages/Activity'), 'Activity');
const ZohoTickets = lazyPage(() => import('./pages/ZohoTickets'), 'ZohoTickets');
const ZohoTicketDetail = lazyPage(() => import('./pages/ZohoTicketDetail'), 'ZohoTicketDetail');
const SupportInbox = lazyPage(() => import('./pages/SupportInbox'), 'SupportInbox');
const AiSupportSessionDetail = lazyPage(() => import('./pages/AiSupportSessionDetail'), 'AiSupportSessionDetail');
const DesignReference = lazyPage(() => import('./pages/DesignReference'), 'DesignReference');
const Advanced = lazyPage(() => import('./pages/Advanced'), 'Advanced');
const Diagnostics = lazyPage(() => import('./pages/Diagnostics'), 'Diagnostics');
const Playbooks = lazyPage(() => import('./pages/Playbooks'), 'Playbooks');
const RequestCenterPage = lazyPage(() => import('./pages/RequestCenter'), 'RequestCenter');
const NotificationsPage = lazyPage(() => import('./pages/Notifications'), 'Notifications');
const SearchPage = lazyPage(() => import('./pages/Search'), 'Search');
const ReleaseNoteDetail = lazyPage(() => import('./pages/ReleaseNoteDetail'), 'ReleaseNoteDetail');
// Exported so the request board / release notes can RequestDetail.preload() before
// the slide View Transition (warms the chunk so the slide shows the real page).
export const RequestDetail = lazyPage(() => import('./pages/RequestDetail'), 'RequestDetail');
const Guides = lazyPage(() => import('./pages/Guides'), 'Guides');
const GuideEditor = lazyPage(() => import('./pages/GuideEditor'), 'GuideEditor');
const GuideAppDetail = lazyPage(() => import('./pages/GuideAppDetail'), 'GuideAppDetail');
const StatusOps = lazyPage(() => import('./pages/StatusOps'), 'StatusOps');
// Exported so the Status Page incident list can StatusIncidentDetail.preload()
// before its slide View Transition (mirrors BacklogItemDetail).
export const StatusIncidentDetail = lazyPage(() => import('./pages/StatusIncidentDetail'), 'StatusIncidentDetail');
const Tenants = lazyPage(() => import('./pages/Tenants'), 'Tenants');
// Exported so the Tenants list / detail pages can preload the next detail
// chunk before the slide View Transition (mirrors BacklogItemDetail).
export const TenantDetail = lazyPage(() => import('./pages/TenantDetail'), 'TenantDetail');
export const TenantResourceGroupDetail = lazyPage(() => import('./pages/TenantResourceGroupDetail'), 'TenantResourceGroupDetail');
export const TenantApplicationDetail = lazyPage(() => import('./pages/TenantApplicationDetail'), 'TenantApplicationDetail');
export const TenantDataStoreDetail = lazyPage(() => import('./pages/TenantDataStoreDetail'), 'TenantDataStoreDetail');

/** Redirect a legacy /discourse/:id link to /topics/:id. */
function DiscourseRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/topics/${id ?? ''}`} replace />;
}

/** Redirect a legacy /knowledge/specs/:id link to /solution-design/:id. */
function PageSpecRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/solution-design/${id ?? ''}`} replace />;
}

/** Redirect a legacy /azure-devops/:id link to /backlog/:id. */
function BacklogItemRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/backlog/${id ?? ''}`} replace />;
}

/** Force the detail page to REMOUNT when the :id changes. Opening a Related item /
 *  Mention is a same-route hop (/backlog/:id → /backlog/:id) that would otherwise
 *  REUSE the mounted page and swap data in place — no old-page → new-page, so the
 *  slide View Transition has nothing to animate between. Keying by :id makes it a
 *  fresh mount, exactly like the kanban list→detail path; the 10-min detail cache +
 *  the prefetch make the remount render instantly (no reload flash). Also falls back
 *  to `params.reId` for routes keyed on `:reId` instead of `:id` (e.g. the tenant
 *  detail routes). */
function RemountById({ children }: { children: ReactNode }) {
  const params = useParams();
  const key = params.id ?? params.reId;
  return <Fragment key={key}>{children}</Fragment>;
}

/** Emits a `page_view` event on every SPA navigation (no-op until telemetry is
 *  enabled). Mounted under the router so it sees client-side route changes. Also
 *  fires the per-user audit "moves" beacon (debounced) so the User administration
 *  detail page's Audit tab can show navigation, and publishes page context to the
 *  embedded Smartguides player (f-00202 bridge; entity.* is enriched per detail page
 *  via the guidesContextStore — f-00203). */
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    track('page_view', { 'app.path': location.pathname, 'app.search': location.search });
    const docTitle = () => (typeof document !== 'undefined' ? document.title : '');
    const skeleton = entitySkeletonFromPath(location.pathname);
    // Publish page + entity skeleton immediately (path/entity accurate now; the title
    // is re-published below once it settles — same record, so enrichment is preserved).
    setGuidesRoute(
      { route: location.pathname + location.search, path: location.pathname, search: location.search, title: docTitle() },
      skeleton,
    );
    // Debounced so rapid transitions coalesce into one move. Best-effort: the
    // beacon swallows its own errors and is a no-op when logged out.
    const path = location.pathname;
    const t = setTimeout(() => {
      void trackActivity(path, docTitle() || undefined);
      // Re-publish once document.title has settled (page.title only; entity kept).
      setGuidesRoute(
        { route: location.pathname + location.search, path: location.pathname, search: location.search, title: docTitle() },
        skeleton,
      );
    }, 800);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);
  return null;
}

/** Subtle full-page fallback while a route chunk loads. */
function RouteFallback() {
  return (
    <div className="page-container" style={{ paddingTop: 'var(--space-6)', color: 'var(--text-tertiary)' }}>
      Loading…
    </div>
  );
}

/** Route-level error boundary (data router). React Router installs its OWN error
 *  boundary at the root of the route tree, which would otherwise swallow route render
 *  crashes with its unstyled default screen and bypass the app's <ErrorBoundary> — so
 *  crashes would stop being reported to the server error store. Attaching this as the
 *  layout route's errorElement re-homes the exact old behavior: self-heal a stale
 *  lazy chunk, report to OpenObserve + the server store, and show the on-brand
 *  "Something went wrong / Reload" fallback (mirrors components/ErrorBoundary). */
function RouteErrorElement() {
  const error = useRouteError();
  useEffect(() => {
    const err = error instanceof Error ? error : new Error(String(error));
    // Stale lazy chunk after a deploy → one loop-guarded reload instead of stranding.
    if (isStaleChunkError(err) && maybeReloadForStaleChunk()) return;
    reportError(err);
    reportClientError(err, { kind: 'error-boundary' });
  }, [error]);
  return (
    <div
      role="alert"
      style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-6)', background: 'var(--bg-primary)' }}
    >
      <div
        style={{ maxWidth: 420, width: '100%', textAlign: 'center', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-8) var(--space-6)', boxShadow: 'var(--shadow-lg)' }}
      >
        <div style={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
          Something went wrong
        </div>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 'var(--leading-normal)', marginBottom: 'var(--space-6)' }}>
          The app hit an unexpected error. Reloading usually clears it.
        </div>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    </div>
  );
}

/** The theme editor opens in its OWN window/route and renders standalone (no app
 *  chrome) so it's a clean editing surface; ThemeApplier still themes it. */
function ThemeEditorRoute() {
  if (useAuthStore.getState().status !== 'authenticated') return <Navigate to="/" replace />;
  return (
    <Suspense fallback={<RouteFallback />}>
      <ThemeApplier />
      <ThemeEditor />
    </Suspense>
  );
}

/** The persistent app shell (chrome + always-mounted overlays). Renders the matched
 *  route via <Outlet/>; this is the data router's ROOT LAYOUT route element.
 *  OnboardingGate overlays the first-run setup wizard on top of the app tree
 *  (fail-open: it renders children normally and only adds the wizard when an
 *  authenticated, cleanly-loaded profile reports it hasn't onboarded). */
function AppShell() {
  // Unified Activity Center + slim topbar dark-launch (default ON). When on, the
  // single SyncActivityBridge poll + the Activity Center tray replace the legacy
  // floating AnalysisPanel dock and SyncLogPanel toast.
  const activityCenter = useActivityCenterFlag();
  // Re-attribute this device's existing push subscription to the signed-in user on
  // load — heals subscriptions stored before per-user attribution existed.
  useEffect(() => { void syncPushSubscription(); }, []);
  return (
    <OnboardingGate>
      <RouteTracker />
      <SlideDirectionBridge />
      <Layout>
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </Layout>
      <ToastContainer />
      {activityCenter ? (
        <>
          {/* Single /api/syncs poll (absorbs SyncLogPanel's load-bearing
              invalidation) + the unified Activity Center surface. */}
          <SyncActivityBridge />
          <ActivityCenter />
        </>
      ) : (
        <>
          <AnalysisPanel />
          <SyncLogPanel />
        </>
      )}
      {/* App-wide realtime sync: live create/update/delete across all clients
          (independent of which sync surface above is active). */}
      <RealtimeBridge />
      <ReloadPrompt />
      <SupportChatWidget />
    </OnboardingGate>
  );
}

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* Standalone theme-editor window — no app chrome. Matched before the layout. */}
      <Route path="/theme-editor" element={<ThemeEditorRoute />} errorElement={<RouteErrorElement />} />
      <Route element={<AppShell />} errorElement={<RouteErrorElement />}>
        <Route index element={<Dashboard />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route
          path="/chat"
          element={
            <RequirePermission perm="conversations.use">
              <AiChat />
            </RequirePermission>
          }
        />
        <Route
          path="/settings"
          element={
            <RequirePermission perm={['settings.manage', 'sync.run', 'maintenance.danger', 'users.manage']}>
              <Settings />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/users/:id"
          element={
            <RequirePermission perm="users.manage">
              <UserDetail />
            </RequirePermission>
          }
        />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/playbooks" element={<Playbooks />} />
        <Route path="/design" element={<DesignReference />} />
        <Route
          path="/advanced"
          element={
            <RequirePermission perm="advanced.view">
              <Advanced />
            </RequirePermission>
          }
        />
        <Route
          path="/diagnostics"
          element={
            <RequirePermission perm="diagnostics.view">
              <Diagnostics />
            </RequirePermission>
          }
        />
        <Route path="/requests" element={<RequestCenterPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        {/* Release notes get their own address so a release can be linked to.
            Top-level, not under /releases/* — that is Release Planning
            (deployment trains), a different domain entirely. (f-01807) */}
        <Route path="/release-notes/:id" element={<ReleaseNoteDetail />} />
        <Route path="/requests/:id" element={<RequestDetail />} />
        <Route path="/topics" element={<Discourse />} />
        <Route path="/topics/:id" element={<RemountById><DiscourseTopic /></RemountById>} />
        {/* Legacy /discourse URLs → /topics (bookmarks). */}
        <Route path="/discourse" element={<Navigate to="/topics" replace />} />
        <Route path="/discourse/:id" element={<DiscourseRedirect />} />
        <Route path="/tickets" element={<ZohoTickets />} />
        <Route path="/tickets/insights" element={<Navigate to="/insights?tab=tickets" replace />} />
        <Route path="/tickets/agents" element={<Navigate to="/insights?tab=agents" replace />} />
        <Route path="/tickets/:id" element={<RemountById><ZohoTicketDetail /></RemountById>} />
        <Route path="/support" element={<SupportInbox />} />
        <Route path="/support/:id" element={<AiSupportSessionDetail />} />
        <Route
          path="/status"
          element={
            <RequirePermission perm={['status.publish', 'status.manage']}>
              <StatusOps />
            </RequirePermission>
          }
        />
        <Route
          path="/status/incidents/:id"
          element={
            <RequirePermission perm={['status.publish', 'status.manage']}>
              <RemountById><StatusIncidentDetail /></RemountById>
            </RequirePermission>
          }
        />
        <Route
          path="/insights"
          element={
            <RequirePermission perm={INSIGHTS_ACCESS_PERMS}>
              <InsightsHub />
            </RequirePermission>
          }
        />
        <Route
          path="/insights/employees/:id"
          element={
            <RequirePermission perm="insights.employees">
              <EmployeeDetail />
            </RequirePermission>
          }
        />
        <Route path="/activity" element={<Activity />} />
        <Route path="/knowledge" element={<KnowledgeBase />} />
        <Route path="/solution-design" element={<SolutionDesign />} />
        <Route path="/solution-design/:id" element={<RemountById><PageSpec /></RemountById>} />
        {/* Legacy /knowledge/specs URLs → /solution-design (bookmarks); the detail
            redirect forwards the :id. The old ?tab=specifications query redirect
            lives in KnowledgeBase.tsx (App routes can't read the query string). */}
        <Route path="/knowledge/specs" element={<Navigate to="/solution-design" replace />} />
        <Route path="/knowledge/specs/:id" element={<PageSpecRedirect />} />
        <Route path="/knowledge/articles/:id/edit" element={<KnowledgeArticleEditor />} />
        <Route path="/knowledge/articles/:slug" element={<KnowledgeArticleReader />} />
        {/* Legacy /documentation URLs → /knowledge (bookmarks). */}
        <Route path="/documentation" element={<Navigate to="/knowledge" replace />} />
        <Route
          path="/guides"
          element={
            <RequirePermission perm="guides.author">
              <Guides />
            </RequirePermission>
          }
        />
        <Route
          path="/guides/apps/:appId"
          element={
            <RequirePermission perm="guides.author">
              <GuideAppDetail />
            </RequirePermission>
          }
        />
        <Route
          path="/guides/:id"
          element={
            <RequirePermission perm="guides.author">
              <GuideEditor />
            </RequirePermission>
          }
        />
        <Route path="/coco-config" element={<CocoConfig />} />
        {/* Per-user PRIVATE triage inbox (server-scoped to the acting user) —
            ungated: every authenticated user has their own. */}
        <Route path="/inbox" element={<CommsInbox />} />
        <Route path="/companies" element={<Companies />} />
        <Route path="/companies/contacts/:id" element={<RemountById><ContactDetail /></RemountById>} />
        <Route path="/companies/deals/:id" element={<RemountById><DealDetail /></RemountById>} />
        <Route path="/companies/leads/:id" element={<RemountById><LeadDetail /></RemountById>} />
        <Route path="/companies/:id" element={<RemountById><CompanyDetail /></RemountById>} />
        <Route path="/backlog" element={<BacklogItems />} />
        <Route path="/backlog/:id" element={<RemountById><BacklogItemDetail /></RemountById>} />
        {/* Back-compat alias for links shared with the brief readable-slug path
            (/backlog/:id/<slug>): renders the same component, and the detail page
            normalizes the address bar back to /backlog/:id?s=…. */}
        <Route path="/backlog/:id/:slug" element={<RemountById><BacklogItemDetail /></RemountById>} />
        {/* Feedback boards — the Backlog area's third tab lives at /backlog?tab=feedback;
            a board's own page is top-level, like /topics/:id and /tickets/:id. The
            :slug variant lets a shared link carry a readable title. (f-01842) */}
        <Route path="/feedback/:id" element={<RemountById><FeedbackDetail /></RemountById>} />
        <Route path="/feedback/:id/:slug" element={<RemountById><FeedbackDetail /></RemountById>} />
        {/* Legacy /azure-devops URLs → /backlog (bookmarks). */}
        <Route path="/azure-devops" element={<Navigate to="/backlog" replace />} />
        <Route path="/azure-devops/:id" element={<BacklogItemRedirect />} />
        <Route path="/tenants" element={<Tenants />} />
        <Route path="/tenants/rg/:reId" element={<RemountById><TenantResourceGroupDetail /></RemountById>} />
        <Route path="/tenants/app/:reId" element={<RemountById><TenantApplicationDetail /></RemountById>} />
        <Route path="/tenants/ds/:reId" element={<RemountById><TenantDataStoreDetail /></RemountById>} />
        <Route path="/tenants/:reId" element={<RemountById><TenantDetail /></RemountById>} />
        <Route path="/entitlements" element={<Entitlements />} />
        <Route path="/releases" element={<ReleasePlanning />} />
        <Route path="/releases/app/:app" element={<ReleaseAppDetail />} />
        <Route path="/releases/tracking/:id" element={<ReleaseTrackingDetail />} />
        <Route path="/releases/:id" element={<ReleaseSession />} />
        <Route path="/commits" element={<CommitTracker />} />
        <Route path="/commits/:id" element={<CommitDetails />} />
        <Route path="/commits/analysis/:id" element={<CommitAnalysis />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </>
  )
);
